import { useRef, useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { Smile } from 'lucide-react'
import { EmojiPicker } from '@/components/chat/EmojiPicker'
import { cx } from '@/lib/cx'
import type { ReactionEmoji } from '@/types/api'
import auth from '@/pages/AuthPage.module.css'
import styles from './ChannelNameInput.module.css'

const MAX = 100
// размеры окна эмодзи — как у .compact в MediaPicker.module.css
const PICKER_W = 352
const PICKER_H = 420

interface Props {
  id: string
  value: string
  onChange: (value: string) => void
  placeholder?: string | undefined
  autoFocus?: boolean | undefined
  required?: boolean | undefined
  disabled?: boolean | undefined
}

/**
 * Поле названия канала или роли с кнопкой эмодзи справа, как у Discord: выбранный эмодзи встаёт туда,
 * где стоял курсор. Только обычные эмодзи — картинки сервера в название не вставить.
 */
export function ChannelNameInput({ id, value, onChange, placeholder, autoFocus, required, disabled }: Props) {
  const input = useRef<HTMLInputElement | null>(null)
  const button = useRef<HTMLButtonElement | null>(null)
  // выделение до открытия окна: фокус уходит в поиск эмодзи, а вставить надо на прежнее место
  const sel = useRef<[number, number] | null>(null)
  const [at, setAt] = useState<{ top: number; left: number; height: number } | null>(null)

  const open = () => {
    const el = input.current
    const r = button.current?.getBoundingClientRect()
    if (!el || !r) return
    sel.current = [el.selectionStart ?? value.length, el.selectionEnd ?? value.length]
    const w = Math.min(PICKER_W, window.innerWidth - 32)
    // под полем; не влезает — над ним; нигде не влезает целиком — где места больше, и ниже ростом
    const below = window.innerHeight - r.bottom - 16
    const above = r.top - 16
    const down = below >= PICKER_H || below >= above
    const height = Math.max(200, Math.min(PICKER_H, down ? below : above))
    setAt({ top: down ? r.bottom + 8 : r.top - 8 - height, left: Math.max(16, Math.min(r.right - w, window.innerWidth - w - 16)), height })
  }

  const pick = (emoji: ReactionEmoji) => {
    if (emoji.id) return
    const [a, b] = sel.current ?? [value.length, value.length]
    const next = value.slice(0, a) + emoji.name + value.slice(b)
    if (next.length > MAX) return
    onChange(next)
    setAt(null)
    const caret = a + emoji.name.length
    requestAnimationFrame(() => {
      input.current?.focus()
      input.current?.setSelectionRange(caret, caret)
    })
  }

  return (
    <div className={styles.wrap}>
      <input
        ref={input}
        id={id}
        className={cx(auth.input, styles.input)}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={MAX}
        placeholder={placeholder}
        autoFocus={autoFocus}
        required={required}
        disabled={disabled}
      />
      <button
        ref={button}
        type="button"
        className={cx(styles.emoji, at && styles.emojiOn)}
        data-tooltip="Эмодзи"
        aria-label="Добавить эмодзи в название"
        aria-expanded={Boolean(at)}
        aria-haspopup="dialog"
        disabled={disabled}
        // окно закрывается по нажатию мимо себя; без этого нажатие на кнопку закрыло бы его и тут же открыло снова
        onMouseDown={(e) => e.stopPropagation()}
        onClick={() => (at ? setAt(null) : open())}
      >
        <Smile size={20} />
      </button>
      {at
        ? createPortal(
            <div className={styles.popover} style={{ top: at.top, left: at.left, '--picker-h': `${at.height}px` } as CSSProperties}>
              <EmojiPicker custom={[]} onPick={pick} onClose={() => setAt(null)} />
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}
